/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        deneignementService.ts
 * @description Service d'acces aux endpoints API des interventions de deneigement
 *              Encapsule tous les appels HTTP lies au cycle de vie des interventions
 *
 * @endpoints   GET    /deneignements           - Liste paginee avec filtres
 *              GET    /deneignements/:id       - Detail d'une intervention
 *              POST   /deneignements           - Creation
 *              PUT    /deneignements/:id       - Modification
 *              DELETE /deneignements/:id       - Suppression
 *              PATCH  /deneignements/:id/affecter   - Affectation equipe
 *              PATCH  /deneignements/:id/demarrer   - Demarrage
 *              PATCH  /deneignements/:id/terminer   - Terminaison
 *              PATCH  /deneignements/:id/valider    - Validation
 *              PATCH  /deneignements/:id/annuler    - Annulation
 *              POST   /deneignements/:id/photos     - Upload photo
 *              GET    /deneignements/:id/pdf        - Telechargement PDF
 *
 * @version     2.1
 * @author      Equipe FALCON
 * =============================================================================
 */

import apiClient from './api';
import type {
  Deneignement,
  CreateDeneignementDTO,
  UpdateDeneignementDTO,
  AffecterEquipeDTO,
  AnnulerDTO,
  DeneignementFilters,
  PaginatedResponse,
  DeneignementHistorique,
} from '@/types';

/** URL de base pour les endpoints deneignement */
const BASE_URL = '/deneignements';

/**
 * Service API pour les interventions de deneigement
 *
 * Fournit toutes les methodes pour interagir avec l'API backend
 * concernant les interventions de deneigement. Chaque methode
 * correspond a un endpoint REST specifique.
 *
 * @example
 * // Lister les interventions avec filtre
 * const interventions = await deneignementService.list({ statut: 'en_cours' });
 *
 * // Creer une intervention
 * const newIntervention = await deneignementService.create({
 *   dateIntervention: '2026-01-27',
 *   compagnieId: 1,
 *   parkingId: 5,
 *   priorite: 'urgente'
 * });
 */
export const deneignementService = {
  /**
   * Liste des interventions avec filtres et pagination
   */
  async list(
    filters: DeneignementFilters = {},
    page = 1,
    limit = 20
  ): Promise<PaginatedResponse<Deneignement>> {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });

    const response = await apiClient.get<PaginatedResponse<Deneignement>>(
      `${BASE_URL}?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Detail d'une intervention
   */
  async get(id: number): Promise<Deneignement> {
    const response = await apiClient.get<Deneignement>(`${BASE_URL}/${id}`);
    return response.data;
  },

  /**
   * Creation d'une intervention
   */
  async create(data: CreateDeneignementDTO): Promise<Deneignement> {
    // Transformer les noms de champs pour le backend
    const payload = {
      dateIntervention: data.dateIntervention,
      heureSouhaitee: data.heureSouhaitee,
      compagnie: data.compagnieId,
      parking: data.parkingId,
      zone: data.zone,
      typeIntervention: data.typeIntervention,
      priorite: data.priorite,
      epaisseurNeige: data.epaisseurNeige,
      immatriculationAvion: data.immatriculationAvion,
      commentaire: data.commentaire,
      confirmerCreation: data.confirmerCreation,
      posteOccupe: data.posteOccupe,
    };
    const response = await apiClient.post<Deneignement>(BASE_URL, payload);
    return response.data;
  },

  /**
   * Modification d'une intervention
   */
  async update(id: number, data: UpdateDeneignementDTO): Promise<Deneignement> {
    const response = await apiClient.put<Deneignement>(`${BASE_URL}/${id}`, data);
    return response.data;
  },

  /**
   * Suppression d'une intervention
   */
  async delete(id: number): Promise<void> {
    await apiClient.delete(`${BASE_URL}/${id}`);
  },

  /**
   * Affectation d'une equipe
   */
  async affecter(id: number, data: AffecterEquipeDTO): Promise<Deneignement> {
    const response = await apiClient.patch<Deneignement>(
      `${BASE_URL}/${id}/affecter`,
      { equipe_id: data.equipeId }
    );
    return response.data;
  },

  /**
   * Change l'equipe d'une intervention deja affectee (reassignation), sans
   * toucher au statut. Distinct de affecter(), qui ne fonctionne qu'au premier
   * passage (en_attente -> affectee).
   */
  async changerEquipe(id: number, data: AffecterEquipeDTO): Promise<Deneignement> {
    const response = await apiClient.patch<Deneignement>(
      `${BASE_URL}/${id}/equipe`,
      { equipe_id: data.equipeId }
    );
    return response.data;
  },

  /**
   * Demarrage de l'intervention
   */
  async demarrer(id: number): Promise<Deneignement> {
    const response = await apiClient.patch<Deneignement>(`${BASE_URL}/${id}/demarrer`);
    return response.data;
  },

  /**
   * Renseigne ou efface l'immatriculation de l'avion present sur l'emplacement.
   * Utilisable par l'operateur terrain a tout moment avant cloture (pas seulement
   * a la creation). Passer `null` pour effacer ("aucun avion present").
   */
  async updateImmatriculation(id: number, immatriculationAvion: string | null): Promise<Deneignement> {
    const response = await apiClient.patch<Deneignement>(
      `${BASE_URL}/${id}/immatriculation`,
      { immatriculationAvion }
    );
    return response.data;
  },

  /**
   * Change l'etat du poste (occupe ou non) et regenere les jalons en
   * consequence. Refuse en 409 si un jalon a deja demarre (RG-05).
   */
  async updateEtatPoste(id: number, posteOccupe: boolean): Promise<Deneignement> {
    const response = await apiClient.patch<Deneignement>(
      `${BASE_URL}/${id}/etat-poste`,
      { posteOccupe }
    );
    return response.data;
  },

  /**
   * Terminaison de l'intervention
   * @param id ID de l'intervention
   * @param options Options pour cloture sans travaux
   */
  async terminer(
    id: number,
    options?: {
      travauxEffectues?: boolean;
      motifNonRealisation?: string;
      commentaireNonRealisation?: string;
    }
  ): Promise<Deneignement> {
    const payload = options ? {
      travaux_effectues: options.travauxEffectues,
      motif_non_realisation: options.motifNonRealisation,
      commentaire_non_realisation: options.commentaireNonRealisation,
    } : {};
    const response = await apiClient.patch<Deneignement>(`${BASE_URL}/${id}/terminer`, payload);
    return response.data;
  },

  /**
   * Validation de l'intervention
   */
  async valider(id: number): Promise<Deneignement> {
    const response = await apiClient.patch<Deneignement>(`${BASE_URL}/${id}/valider`);
    return response.data;
  },

  /**
   * Annulation de l'intervention
   */
  async annuler(id: number, data: AnnulerDTO): Promise<Deneignement> {
    const response = await apiClient.patch<Deneignement>(`${BASE_URL}/${id}/annuler`, data);
    return response.data;
  },

  /**
   * Upload de photo
   */
  async uploadPhoto(id: number, type: 'avant' | 'apres', file: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append('photo', file);
    formData.append('type', type);

    const response = await apiClient.post<{ success: boolean; url: string }>(
      `${BASE_URL}/${id}/photos`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return { url: response.data.url };
  },

  /**
   * Suppression de photo
   */
  async deletePhoto(id: number, type: 'avant' | 'apres'): Promise<void> {
    await apiClient.delete(`${BASE_URL}/${id}/photos/${type}`);
  },

  /**
   * Historique des changements
   */
  async getHistorique(id: number): Promise<DeneignementHistorique[]> {
    const response = await apiClient.get<DeneignementHistorique[]>(
      `${BASE_URL}/${id}/historique`
    );
    return response.data;
  },

  /**
   * Telechargement du PDF
   */
  async downloadPdf(id: number): Promise<Blob> {
    const response = await apiClient.get(`${BASE_URL}/${id}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

export default deneignementService;
